import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselProps<T> {
    items: T[];
    renderItem: (item: T) => React.ReactNode;
    autoPlay?: boolean;
    interval?: number;
    itemsPerView?: {
        mobile?: number;
        tablet?: number;
        desktop?: number;
    };
    gap?: string;
}

export function Carousel<T>({
    items,
    renderItem,
    autoPlay = false,
    interval = 4000,
    itemsPerView = { mobile: 1, tablet: 2, desktop: 3 },
    gap = "1.5rem"
}: CarouselProps<T>) {
    const autoplay = useMemo(
        () =>
            autoPlay
                ? Autoplay({
                      delay: interval,
                      stopOnInteraction: true
                  })
                : undefined,
        [autoPlay, interval]
    );

    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            align: "start",
            loop: false
        },
        autoplay ? [autoplay] : []
    );

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [slidesInView, setSlidesInView] = useState(1);

    useEffect(() => {
        const updateSlides = () => {
            if (window.innerWidth < 640) {
                setSlidesInView(itemsPerView.mobile ?? 1);
            } else if (window.innerWidth < 1024) {
                setSlidesInView(itemsPerView.tablet ?? 2);
            } else {
                setSlidesInView(itemsPerView.desktop ?? 3);
            }
        };

        updateSlides();
        window.addEventListener("resize", updateSlides);
        return () => window.removeEventListener("resize", updateSlides);
    }, [itemsPerView]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
    }, [emblaApi, onSelect]);

    /* -----------------------------
     Controls
  ------------------------------ */
    const scrollPrev = () => emblaApi?.scrollPrev();
    const scrollNext = () => emblaApi?.scrollNext();
    const scrollTo = (i: number) => emblaApi?.scrollTo(i);

    const snapCount = emblaApi?.scrollSnapList().length ?? 0;

    return (
        <div className="relative w-full">
            {/* Viewport */}
            <div ref={emblaRef} className="overflow-hidden p-2">
                {/* Container */}
                <div
                    className="flex"
                    style={{
                        gap
                    }}
                >
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className="flex-shrink-0"
                            style={{
                                width: `calc((100% - ${gap} * ${slidesInView - 1}) / ${slidesInView})`
                            }}
                        >
                            {renderItem(item)}
                        </div>
                    ))}
                </div>
            </div>

            {/* Controls */}
            <div className="mt-8 flex items-center justify-center gap-6">
                <button onClick={scrollPrev} disabled={!emblaApi?.canScrollPrev()} className="disabled:opacity-30">
                    <ChevronLeft />
                </button>

                <button onClick={scrollNext} disabled={!emblaApi?.canScrollNext()} className="disabled:opacity-30">
                    <ChevronRight />
                </button>
            </div>
        </div>
    );
}
